import { Routes, Route, Navigate, useParams } from 'react-router-dom';

/** NL meervoud → canonieke route */
function RedirectProjectResultaten() {
  const { projectId } = useParams<{ projectId: string }>();
  return <Navigate to={`/projects/${projectId}/resultaat`} replace />;
}
import { Layout } from './components/Layout';
import { ImageCopyProtection } from './components/ImageCopyProtection';
import { AnalyticsInit } from './components/AnalyticsInit';
import { AssistantChat } from './components/AssistantChat';
import { Landing } from './features/Landing';
import { ProductDemo } from './features/ProductDemo';
import { Dashboard } from './features/Dashboard';
import { OnboardingWizard } from './features/OnboardingWizard';
import { ProjectNew } from './features/ProjectNew';
import { ProjectIntake } from './features/ProjectIntake';
import { ProjectDetail } from './features/ProjectDetail';
import { GegevensTab } from './features/project-detail/GegevensTab';
import { PostenTab } from './features/project-detail/PostenTab';
import { ResultaatTab } from './features/project-detail/ResultaatTab';
import { RapportageTab } from './features/project-detail/RapportageTab';
import { Contact } from './features/Contact';
import { Privacy } from './features/Privacy';
import { Terms } from './features/Terms';
import { Cookies } from './features/Cookies';
import { Pricing } from './features/Pricing';
import { PaymentMethods } from './features/PaymentMethods';
import { Blog } from './features/Blog';
import { BlogPost } from './features/BlogPost';
import { Reviews } from './features/Reviews';
import { HelpTekeningFlow } from './features/HelpTekeningFlow';
import { HelpRapportageExport } from './features/HelpRapportageExport';
import { HelpLibrary } from './features/HelpLibrary';
import { HelpContractStatus } from './features/HelpContractStatus';
import { Login } from './features/Login';
import { AcceptInvite } from './features/AcceptInvite';
import { ForgotPassword } from './features/ForgotPassword';
import { ResetPassword } from './features/ResetPassword';
import { Account } from './features/Account';
import { Troubleshoot } from './features/Troubleshoot';
import { Status } from './features/Status';
import { ApiTools } from './features/ApiTools';
import { NotFound } from './features/NotFound';
import { AdminGuard } from './components/AdminGuard';
import { RequireAccount } from './components/RequireAccount';
import { RequireAdmin } from './components/RequireAdmin';
import { AdminLayout } from './features/admin/AdminLayout';
import { AdminDashboard } from './features/admin/AdminDashboard';
import { AdminCms } from './features/admin/AdminCms';
import { AdminCmsReviews } from './features/admin/AdminCmsReviews';
import { AdminBlogList } from './features/admin/AdminBlogList';
import { AdminBlogEditor } from './features/admin/AdminBlogEditor';
import { AdminAnalytics } from './features/admin/AdminAnalytics';
import { AdminBilling } from './features/admin/AdminBilling';
import { AdminBillingPlatform } from './features/admin/AdminBillingPlatform';
import { AdminTenants } from './features/admin/AdminTenants';
import { AdminUitnodigingen } from './features/admin/AdminUitnodigingen';
import { AdminLibraryCoverage } from './features/admin/AdminLibraryCoverage';
import { AdminSeoHealth } from './features/admin/AdminSeoHealth';
import { AdminSeoPages } from './features/admin/AdminSeoPages';
import { AdminMeldingen } from './features/admin/AdminMeldingen';
import { BillingPortal } from './features/BillingPortal';
import { AccountLaborRates } from './features/AccountLaborRates';

/**
 * Toegang: publiek = /, /demo, /blog, /betaalmethoden, contact, privacy, terms, cookies, prijzen, status, troubleshoot, help/*, login (+ publieke assistent op /, /demo en /blog).
 * Account verplicht = dashboard, onboarding, projecten. `/account/billing` = accountbeheerder van de tenant.
 * `/admin` = Platformbeheer, uitsluitend `superadmin`.
 */
function App() {
  return (
    <Layout>
      <ImageCopyProtection />
      <AnalyticsInit />
      <AssistantChat />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/demo" element={<ProductDemo />} />
        <Route
          path="/dashboard"
          element={
            <RequireAccount>
              <Dashboard />
            </RequireAccount>
          }
        />
        <Route
          path="/onboarding"
          element={
            <RequireAccount>
              <OnboardingWizard />
            </RequireAccount>
          }
        />
        <Route
          path="/projects/intake"
          element={
            <RequireAccount>
              <ProjectIntake />
            </RequireAccount>
          }
        />
        <Route
          path="/projects/new"
          element={
            <RequireAccount>
              <ProjectNew />
            </RequireAccount>
          }
        />
        <Route
          path="/projects/:projectId"
          element={
            <RequireAccount>
              <ProjectDetail />
            </RequireAccount>
          }
        >
          <Route index element={<Navigate to="gegevens" replace />} />
          <Route path="gegevens" element={<GegevensTab />} />
          <Route path="posten" element={<PostenTab />} />
          <Route path="resultaat" element={<ResultaatTab />} />
          <Route path="resultaten" element={<RedirectProjectResultaten />} />
          <Route path="rapportage" element={<RapportageTab />} />
        </Route>
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/prijzen" element={<Pricing />} />
        <Route path="/betaalmethoden" element={<PaymentMethods />} />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/help/tekening" element={<HelpTekeningFlow />} />
        <Route path="/help/export" element={<HelpRapportageExport />} />
        <Route path="/help/bibliotheek" element={<HelpLibrary />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/login" element={<Login />} />
        <Route path="/uitnodiging" element={<AcceptInvite />} />
        <Route path="/register" element={<Navigate to="/login?register=1" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/account"
          element={
            <RequireAccount>
              <Account />
            </RequireAccount>
          }
        />
        <Route path="/troubleshoot" element={<Troubleshoot />} />
        <Route path="/status" element={<Status />} />
        <Route
          path="/account/billing"
          element={
            <RequireAdmin>
              <BillingPortal />
            </RequireAdmin>
          }
        />
        <Route
          path="/account/labor-rates"
          element={
            <RequireAdmin>
              <AccountLaborRates />
            </RequireAdmin>
          }
        />
        <Route path="/admin" element={<AdminGuard />}>
          <Route element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="meldingen" element={<AdminMeldingen />} />
            <Route path="cms" element={<AdminCms />} />
            <Route path="cms/reviews" element={<AdminCmsReviews />} />
            <Route path="cms/blog" element={<AdminBlogList />} />
            <Route path="cms/blog/nieuw" element={<AdminBlogEditor />} />
            <Route path="cms/blog/:slug" element={<AdminBlogEditor />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="seo-health" element={<AdminSeoHealth />} />
            <Route path="seo-pages" element={<AdminSeoPages />} />
            <Route path="billing" element={<AdminBilling />} />
            <Route path="billing-platform" element={<AdminBillingPlatform />} />
            <Route path="tenants" element={<AdminTenants />} />
            <Route path="uitnodigingen" element={<AdminUitnodigingen />} />
            <Route path="library-coverage" element={<AdminLibraryCoverage />} />
            <Route path="api-tools" element={<ApiTools />} />
            <Route path="contract-status" element={<HelpContractStatus />} />
          </Route>
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
}

export default App;
